/**
 * Maps geometry/config parameters to REAL SKUs from the MASTER-rose catalog.
 *
 * Background — R1 (bugs critiques):
 *   The previous implementation hard-coded synthetic SKUs that did not match
 *   real catalog products: pattes were all "F04003" (which is actually a 49.5"
 *   X-bracing, not a leg!), treads ignored widthIn, turn-landings claimed
 *   F01007 (54×54) while geometry rendered 48×48.
 *
 * Catalog SKUs verified against
 *   packages/catalog-adapter/data/catalog.v1.json
 *
 *   F01 = PLATEFORME (platform/landing)
 *   F02 = TROTTOIR (tread) — PLEIN only (no MAILLES tread SKUs exist)
 *   F04 = ENSEMBLE PATTE (leg)
 *   F05 = TRANSITION SOL (ground transition)
 *   MTC = TUBE CARRÉ (square tube, used for corner posts)
 *   QRAL = RIVET ALUMINIUM
 *   QRSS = RIVET STAINLESS STEEL
 *   QBH  = BOULON HEXAGONAL
 */

import type { Floor } from '@gorampe/catalog-adapter';

// ============================================================================
// PATTE (F04 family) — fixes Bug 1
// ============================================================================

/**
 * Map heightIn → ENSEMBLE PATTE SKU.
 *
 * Catalog F04008..F04019 = ENSEMBLE PATTE 8" / 12" / 18" / 24" / ... / 72".
 * Standard catalog heights: 8, 12, 18, 24, 30, 36, 42, 48, 54, 60, 66, 72.
 *
 * We pick the smallest patte SKU whose height is >= the requested heightIn
 * (so a 22"-tall ramp uses a 24" patte trimmed in-shop, not a 18" one which
 * would be too short).
 */
export function selectPatteSku(heightIn: number): string {
  const heightMap: Array<{ maxH: number; sku: string }> = [
    { maxH: 8, sku: 'F04008' }, //  8"
    { maxH: 12, sku: 'F04009' }, // 12"
    { maxH: 18, sku: 'F04010' }, // 18"
    { maxH: 24, sku: 'F04011' }, // 24"
    { maxH: 30, sku: 'F04012' }, // 30"
    { maxH: 36, sku: 'F04013' }, // 36"
    { maxH: 42, sku: 'F04014' }, // 42"
    { maxH: 48, sku: 'F04015' }, // 48"
    { maxH: 54, sku: 'F04016' }, // 54"
    { maxH: 60, sku: 'F04017' }, // 60"
    { maxH: 66, sku: 'F04018' }, // 66"
    { maxH: Infinity, sku: 'F04019' }, // 72"
  ];
  for (const m of heightMap) {
    if (heightIn <= m.maxH) return m.sku;
  }
  return 'F04019';
}

// ============================================================================
// TRANSITION (F05 family)
// ============================================================================

/**
 * Map widthIn + floor → TRANSITION SOL SKU.
 *
 * Catalog F05:
 *   F05001 = TRANSITION SOL PLANCHER MAILLES 36"
 *   F05002 = TRANSITION SOL PLANCHER MAILLES 42"
 *   F05003 = TRANSITION SOL PLANCHER PLEIN   30"
 *   F05004 = TRANSITION SOL PLANCHER PLEIN   36"
 *   F05005 = TRANSITION SOL PLANCHER PLEIN   46"  (close enough for 48"-wide ramps)
 *
 * Ramp widthIn is always 36 or 48 (per RampConfig).
 */
export function selectTransitionSku(widthIn: number, floor: Floor): string {
  if (floor === 'plein') {
    if (widthIn <= 33) return 'F05003'; // 30" plein
    if (widthIn <= 42) return 'F05004'; // 36" plein
    return 'F05005'; // 46" plein (≈48")
  }
  // mailles
  if (widthIn <= 39) return 'F05001'; // 36" mailles
  return 'F05002'; // 42" mailles
}

// ============================================================================
// PLATFORM (F01 family) — fixes Bug 3
// ============================================================================

/**
 * Map (widthIn × depthIn, floor, kind) → PLATEFORME SKU.
 *
 * Catalog F01 (verified in catalog.v1.json):
 *   F01001 = PLATEFORME 48"×48", MAILLES
 *   F01002 = PLATEFORME 48"×60", MAILLES
 *   F01003 = PLATEFORME 54"×54", MAILLES
 *   F01004 = PLATEFORME 60"×60", MAILLES
 *   F01005 = PLATEFORME 48"×48", PLEIN          (LEGACY — pre-2026-05 default)
 *   F01006 = PLATEFORME 48"×60", PLEIN
 *   F01007 = PLATEFORME 54"×54", PLEIN
 *   F01008 = PLATEFORME 60"×60", PLEIN          (PDF officiel default 5×5)
 *   F01009 = PLATEFORME 60"×96", PLEIN          (PDF officiel option 5×8)
 *   F01010 = PLATEFORME 60"×132", PLEIN
 *
 * PDF officiel GoRampe "Configurations de rampes" mandates 5×5 (60×60) or
 * 5×8 (60×96) platforms. The 48×48 SKU is kept only for explicit overrides;
 * the layouts no longer emit 48 platforms by default (see constants.ts).
 *
 * NOTE on mailles:
 *   Catalog has no 60×96 / 60×132 MAILLES variant — for the 96/132 cases
 *   we always return the PLEIN SKU (acceptable for current product mix).
 */
export function selectPlatformSku(
  widthIn: number,
  depthIn: number,
  floor: Floor,
  // kind retained for future use (e.g. when rest-landing dimensions differ from head)
  _kind: 'head' | 'turn-landing' | 'rest-landing' = 'head',
): string {
  const w = Math.round(widthIn);
  const d = Math.round(depthIn);

  // 60×132 (PLEIN only)
  if (w === 60 && d >= 120) return 'F01010';
  // 60×96 (PLEIN only — PDF officiel option 5×8)
  if (w === 60 && d >= 84) return 'F01009';

  // 60×60 (PDF officiel default 5×5)
  if (w === 60 && d === 60) return floor === 'plein' ? 'F01008' : 'F01004';

  // 54×54
  if (w === 54 && d === 54) return floor === 'plein' ? 'F01007' : 'F01003';

  // 48×60
  if (Math.abs(w - 48) <= 6 && Math.abs(d - 60) <= 6) {
    return floor === 'plein' ? 'F01006' : 'F01002';
  }

  // Default 48×48 LEGACY (kept for backward compat / explicit override only).
  // New layouts emit 60×60 by default, so this branch is hit only for legacy
  // configurations or non-standard explicit dimensions.
  return floor === 'plein' ? 'F01005' : 'F01001';
}

// ============================================================================
// TREAD (F02 family) — fixes Bug 2 (widthIn was ignored)
// ============================================================================

/**
 * Map (widthIn, lengthIn, floor) → TROTTOIR SKU.
 *
 * Catalog F02 (MASTER-rose) only contains PLEIN trottoirs:
 *   F02013 = TROTTOIR 36"×24"  PLEIN
 *   F02014 = TROTTOIR 36"×36"  PLEIN
 *   F02015 = TROTTOIR 36"×48"  PLEIN
 *   F02016 = TROTTOIR 36"×60"  PLEIN
 *   F02100 = TROTTOIR XL-GO 48 3/8"×24"  PLEIN
 *   F02101 = TROTTOIR XL-GO 48 3/8"×36"  PLEIN
 *   F02102 = TROTTOIR XL-GO 48 3/8"×48"  PLEIN
 *   F02103 = TROTTOIR XL-GO 48 3/8"×60"  PLEIN
 *   F02104 = TROTTOIR XL-GO 48 3/8"×72"  PLEIN
 *   F02105 = TROTTOIR XL-GO 48 3/8"×96"  PLEIN
 *
 * NOTE: No 30"-wide trottoirs and no MAILLES trottoirs in MASTER-rose.
 *       For `floor='mailles'` we still emit the PLEIN SKU — the "mailles"
 *       choice is currently a visual/legacy distinction only at the tread
 *       level. The catalog adapter MUST decide whether to remap downstream.
 *       (Concern documented in the commit message.)
 */
export function selectTreadSku(widthIn: number, lengthIn: number, _floor: Floor = 'plein'): string {
  const w = roundToStandardWidth(widthIn);
  const l = roundToStandardTreadLength(lengthIn);

  // 36" wide → F02013-F02016
  if (w === 36) {
    if (l === 24) return 'F02013';
    if (l === 36) return 'F02014';
    if (l === 48) return 'F02015';
    return 'F02016'; // 60" and longer fall back to 60"
  }

  // 48" wide → XL-GO F02100-F02105
  if (w === 48) {
    if (l === 24) return 'F02100';
    if (l === 36) return 'F02101';
    if (l === 48) return 'F02102';
    if (l === 60) return 'F02103';
    if (l === 72) return 'F02104';
    return 'F02105'; // 96"
  }

  // Fallback (should not happen: ramp widthIn is 36 or 48)
  return 'F02016';
}

function roundToStandardWidth(widthIn: number): 36 | 48 {
  // Ramp widthIn is constrained to 36 | 48 by RampConfig; map anything else
  // to the closest available trottoir width.
  return widthIn <= 42 ? 36 : 48;
}

function roundToStandardTreadLength(lengthIn: number): 24 | 36 | 48 | 60 | 72 | 96 {
  if (lengthIn <= 30) return 24;
  if (lengthIn <= 42) return 36;
  if (lengthIn <= 54) return 48;
  if (lengthIn <= 66) return 60;
  if (lengthIn <= 84) return 72;
  return 96;
}

// ============================================================================
// CORNER POST (MTC120125125-NNN family)
// ============================================================================

/**
 * Map heightIn → MTC corner post SKU.
 *
 * Catalog: MTC120125125<LLL> = TUBE CARRÉ 0.120"×1¼"×1¼"×LLL"
 *   Standard lengths in inches: 4¾, 8, 10¾, 12, 18, 22¾, 24, 30, 34¾, 36,
 *   38¾, 40, 40¾, 42, 46-3/16, 46¾, 48, 54, 58¾, 60, 66, 72.
 *
 * The suffix is `LLL` zero-padded to 3 digits (e.g. 24" → "024").
 */
export function selectCornerPostSku(heightIn: number): string {
  // Whole-inch standard lengths suitable for vertical corner posts.
  const standardLengths = [8, 12, 18, 24, 30, 36, 42, 48, 54, 60, 66, 72];
  // Pick the smallest length that is >= requested heightIn (so the post is
  // never too short). For heights above 72", fall back to 72".
  const chosen = standardLengths.find((L) => L >= heightIn) ?? 72;
  return `MTC120125125${String(chosen).padStart(3, '0')}`;
}

// ============================================================================
// RIVET (QRAL / QRSS family) — material reference also fixes Bug 4
// ============================================================================

export type RivetGeometry =
  | 'rivet-dome-3-16'
  | 'rivet-dome-1-4'
  | 'rivet-countersunk-3-16'
  | 'rivet-countersunk-1-4';
export type RivetMaterial = 'alu' | 'ss';

/**
 * Map (geometry, material) → RIVET SKU.
 *
 * Catalog actuals (only 3 QRAL and 5 QRSS exist in MASTER-rose):
 *   QRAL188376500TB = RIVET ALU 3/16" × 0.376-0.500 TÊTE BOMBÉE
 *   QRAL188376500PT = RIVET ALU 3/16" × 0.376-0.500 PETITE TÊTE
 *   QRAL188626750TB = RIVET ALU 3/16" × 0.626-0.750 TÊTE BOMBÉE
 *   QRSS188236335TB = RIVET SS  3/16" × 0.236-0.335 TÊTE BOMBÉE
 *   QRSS188334433TB = RIVET SS  3/16" × 0.334-0.433 TÊTE BOMBÉE
 *   QRSS250267385TB = RIVET SS  1/4"  × 0.267-0.385 TÊTE BOMBÉE
 *   QRSS250425503TB = RIVET SS  1/4"  × 0.425-0.503 TÊTE BOMBÉE
 *   QRSS250503582TB = RIVET SS  1/4"  × 0.503-0.582 TÊTE BOMBÉE
 *
 * Catalog has NO countersunk rivets — countersunk variants fall back to
 * the corresponding TB (tête bombée) SKU as the closest real product.
 */
export function selectRivetSku(geometry: RivetGeometry, material: RivetMaterial = 'alu'): string {
  if (material === 'alu') {
    // No 1/4" alu rivet exists — fallback to 3/16" alu TB.
    return 'QRAL188376500TB';
  }
  // SS
  if (geometry.endsWith('1-4')) return 'QRSS250425503TB';
  return 'QRSS188334433TB';
}

// ============================================================================
// BOLT (QBHSS family)
// ============================================================================

export type BoltGeometry = 'bolt-hex-1-4-20' | 'bolt-hex-5-16-18';

/**
 * Map geometry → BOULON HEX SKU.
 * Currently only 5/16"-18 SS bolts are catalogued (QBHSS313XXX). For 1/4"-20
 * we fall back to the 1" 5/16"-18 as a best-effort.
 */
export function selectBoltSku(geometry: BoltGeometry, _material: 'ss' | 'alu' = 'ss'): string {
  // Default to 1" length — production system picks the right length per joint.
  return 'QBHSS313100';
}
